"use client"
import { useState } from "react"
import { format } from "date-fns"
import { CalendarIcon, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

/** 'YYYY-MM-DD' <-> local Date, so a picked day never shifts with the viewer's timezone. */
const parseYmd = (value?: string): Date | undefined => {
  if (!value) return undefined
  const [y, m, d] = value.slice(0, 10).split("-").map(Number)
  return y && m && d ? new Date(y, m - 1, d) : undefined
}
const toYmd = (date: Date) => format(date, "yyyy-MM-dd")

interface DatePickerProps {
  /** Date-object API (original). */
  date?: Date
  onDateChange?: (date: Date | undefined) => void
  /** String API: 'YYYY-MM-DD' in, 'YYYY-MM-DD' out ('' when cleared). Preferred for forms and API payloads. */
  value?: string
  onChange?: (value: string) => void
  min?: string
  max?: string
  placeholder?: string
  className?: string
  disabled?: boolean
  clearable?: boolean
  required?: boolean
  /** Match dark drawer/forms. */
  dark?: boolean
  "aria-label"?: string
}

export function DatePicker({
  date, onDateChange, value, onChange, min, max, placeholder = "Pick a date", className, disabled, clearable, required, dark, ...rest
}: DatePickerProps) {
  const [open, setOpen] = useState(false)
  const controlledByString = onChange !== undefined || value !== undefined
  const selected = controlledByString ? parseYmd(value) : date
  const minDate = parseYmd(min)
  const maxDate = parseYmd(max)
  const thisYear = new Date().getFullYear()

  const pick = (d?: Date) => {
    if (controlledByString) onChange?.(d ? toYmd(d) : "")
    else onDateChange?.(d)
    if (d) setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label={rest["aria-label"]}
            aria-required={required}
            className={cn(
              "flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs transition focus:outline-none focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50",
              dark ? "border-gray-700 bg-gray-900 text-white" : "border-gray-300 bg-white text-gray-900",
              !selected && (dark ? "text-gray-400" : "text-gray-500"),
              clearable && selected && "pr-8",
              className,
            )}
          >
            <CalendarIcon className="h-4 w-4 shrink-0 opacity-70" />
            <span className="truncate">{selected ? format(selected, "dd MMM yyyy") : placeholder}</span>
          </button>
        </PopoverTrigger>
        {clearable && selected && !disabled && (
          <button
            type="button"
            aria-label="Clear date"
            onClick={() => pick(undefined)}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-700"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <PopoverContent className="w-auto border-gray-200 bg-white p-0 shadow-lg z-[100005]" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={pick}
          defaultMonth={selected ?? maxDate ?? minDate}
          captionLayout="dropdown-buttons"
          fromYear={minDate ? minDate.getFullYear() : thisYear - 80}
          toYear={maxDate ? maxDate.getFullYear() : thisYear + 15}
          disabled={[...(minDate ? [{ before: minDate }] : []), ...(maxDate ? [{ after: maxDate }] : [])]}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}

export default DatePicker
